/** Mon–Fri local office window (09:00–18:00) minus a fixed unpaid lunch hour. */
export const OFFICE_DAY_START_HOUR = 9;
export const OFFICE_DAY_END_HOUR = 18;
/** Unpaid lunch break — assumed 13:00–14:00 local time. */
export const OFFICE_LUNCH_START_HOUR = 13;
export const OFFICE_LUNCH_END_HOUR = 14;

export function isOfficeWorkday(d: Date): boolean {
  const dow = d.getDay();
  return dow >= 1 && dow <= 5;
}

/** Whether `d` falls inside the office window on a weekday (local time), excluding lunch. */
export function isOfficeTimeActive(d: Date): boolean {
  if (!isOfficeWorkday(d)) return false;
  const h = d.getHours();
  if (h < OFFICE_DAY_START_HOUR || h >= OFFICE_DAY_END_HOUR) return false;
  if (h >= OFFICE_LUNCH_START_HOUR && h < OFFICE_LUNCH_END_HOUR) return false;
  return true;
}

function officeWindowForLocalDay(day: Date): { start: Date; end: Date } | null {
  if (!isOfficeWorkday(day)) return null;
  return {
    start: new Date(day.getFullYear(), day.getMonth(), day.getDate(), OFFICE_DAY_START_HOUR, 0, 0, 0),
    end: new Date(day.getFullYear(), day.getMonth(), day.getDate(), OFFICE_DAY_END_HOUR, 0, 0, 0),
  };
}

function lunchWindowForLocalDay(day: Date): { start: Date; end: Date } | null {
  if (!isOfficeWorkday(day)) return null;
  return {
    start: new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      OFFICE_LUNCH_START_HOUR,
      0,
      0,
      0,
    ),
    end: new Date(day.getFullYear(), day.getMonth(), day.getDate(), OFFICE_LUNCH_END_HOUR, 0, 0, 0),
  };
}

function overlapMs(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): number {
  const start = Math.max(aStart.getTime(), bStart.getTime());
  const end = Math.min(aEnd.getTime(), bEnd.getTime());
  return Math.max(0, end - start);
}

/** Milliseconds within office hours in the half-open interval [rangeStart, rangeEnd). */
export function officeMsInRange(rangeStart: Date, rangeEnd: Date): number {
  if (rangeEnd <= rangeStart) return 0;

  let total = 0;
  const cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  const last = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());

  while (cur.getTime() <= last.getTime()) {
    const win = officeWindowForLocalDay(cur);
    if (win) {
      let dayMs = overlapMs(rangeStart, rangeEnd, win.start, win.end);
      const lunch = lunchWindowForLocalDay(cur);
      if (lunch) {
        dayMs -= overlapMs(rangeStart, rangeEnd, lunch.start, lunch.end);
      }
      total += Math.max(0, dayMs);
    }
    cur.setDate(cur.getDate() + 1);
  }

  return total;
}

export function officeSecondsInRange(rangeStart: Date, rangeEnd: Date): number {
  return Math.floor(officeMsInRange(rangeStart, rangeEnd) / 1000);
}

export function officeMinutesInRange(rangeStart: Date, rangeEnd: Date): number {
  return Math.max(0, Math.round(officeMsInRange(rangeStart, rangeEnd) / 60_000));
}
